import { VStack, HStack, Divider, FlatList } from "@gluestack-ui/themed";

import { useState, useMemo, useEffect } from "react";

import { Button } from "@repo/core/components/core/Button";
import { Input } from "@repo/core/components/core/Input";
import { EmptyState } from "@repo/core/components/layout/EmptyState";
import { SelectItem } from "@repo/core/components/core/Select/components/SelectList/selectItem";

import { SelectListProps } from "./types";

const removeAccents = (str: string) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const SelectListScreen = ({
  items,
  onChange,
  isMultiple,
  isSearchable,
  value,
  onCancel,
  onPendingSingleSelect
}: SelectListProps) => {
  const [filter, setFilter] = useState("");

  const currentItems = useMemo(() => {
    if (filter) {
      const normalizedFilter = removeAccents(filter.toLowerCase());
      return items.filter((item) =>
        removeAccents(item.label.toLowerCase()).includes(normalizedFilter)
      );
    }
    return items;
  }, [filter, items]);

  const [currentValue, setCurrentValue] = useState<string | string[]>(() => {
    if (value) return value;
    return isMultiple ? [] : "";
  });

  // Sync currentValue with value prop when it changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setCurrentValue(value);
    } else {
      setCurrentValue(isMultiple ? [] : "");
    }
  }, [value, isMultiple]);

  const handleOnChange = (newValue: string) => {
    if (isMultiple) {
      const alreadySelected = (currentValue as string[]).includes(newValue);

      if (alreadySelected) {
        const newValues = (currentValue as string[]).filter(
          (item) => item !== newValue
        );
        setCurrentValue(newValues);
        return;
      }

      setCurrentValue((prev) => [...(prev as string[]), newValue]);
      return;
    }

    const finalValue = newValue;
    setCurrentValue(finalValue);
    if (onPendingSingleSelect) {
      onPendingSingleSelect(finalValue);
      onCancel();
    } else {
      onChange(finalValue);
      onCancel();
    }
  };

  const handleConfirm = () => {
    onChange(currentValue);
    onCancel();
  };

  return (
    <VStack flex={1} px="$4" pb="$4" pt="$2">
      {isSearchable ? (
        <Input
          name="find-term"
          placeholder="Pesquisar"
          onChange={setFilter}
          value={filter}
          rightIcon={{
            name: "magnifying-glass"
          }}
        />
      ) : null}
      <HStack flex={1} p="$4">
        {currentItems?.length ? (
          <FlatList
            data={currentItems}
            flex={1}
            ItemSeparatorComponent={() => <Divider />}
            showsVerticalScrollIndicator={false}
            // Glustack not accepte type of renderItem
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderItem={({ item }: any) => (
              <SelectItem
                item={item}
                onSelectItem={handleOnChange}
                selectedValues={currentValue}
                isMultiple={isMultiple}
                onCloseModal={onCancel}
              />
            )}
            // Glustack not accepte type of keystractor the default is unkwon
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            keyExtractor={(item: any) => item.value}
          />
        ) : (
          <EmptyState height="100%" />
        )}
      </HStack>
      <VStack w="$full" py="$4">
        <Divider />
      </VStack>
      <VStack gap="$2" px="$4" pb="$6">
        {isMultiple ? (
          <VStack w="$full">
            <Button text="Selecionar" onPress={handleConfirm} />
          </VStack>
        ) : null}
        <VStack w="$full">
          <Button
            variant="outline"
            action="darker-secondary"
            text="Cancelar"
            onPress={onCancel}
          />
        </VStack>
      </VStack>
    </VStack>
  );
};
